/**
 * Modal - the pop-up box used for confirmations and short forms
 * -------------------------------------------------------------
 * Handles the things that are easy to forget: closing on Escape, closing when
 * you click the dark background, and stopping the page behind from scrolling.
 *
 * IT ALSO SHOWS ITS OWN ERRORS, and that is not a convenience - see `error`
 * below. A form in here that reports its failures to the page behind reports
 * them to nobody.
 */

import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

import { useI18n } from '../hooks/useI18n';

export default function Modal({ open, onClose, title, children, actions = null, error = '' }) {
  const { t } = useI18n();
  const errorRef = useRef(null);
  // Close on the Escape key, and freeze the page behind while open.
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  /**
   * Put a new error where somebody will actually see it.
   *
   * This box scrolls inside itself (`max-height` plus `overflow-y: auto` on
   * .modal), and a long form on a phone leaves somebody scrolled to the
   * bottom, next to the button they just pressed. An error drawn at the top of
   * the box would be off the screen they are looking at, which is the same
   * silence as not showing it at all.
   *
   * THE THING THAT ACTUALLY MADE THIS FAIL is not in this file - it is
   * `overflow-anchor: none` on .modal in components.css, and it must stay.
   * Inserting the alert ABOVE where somebody is scrolled triggers the
   * browser's scroll anchoring, which pushes the scroll position down by
   * exactly the height of what was inserted, to keep the view steady. That is
   * usually helpful and here it is precisely wrong: it cancels out the scroll
   * that puts the message on screen. Measured while fixing it - scrollTop went
   * 183 to 273 as the alert appeared, and the message ended up 172px above the
   * top of the window.
   *
   * TWO SMALLER CHOICES, both deliberate:
   *
   * - **No requestAnimationFrame.** An earlier version wrapped this in one, on
   *   a guess about layout timing. useEffect already runs after React has put
   *   the alert in the DOM, so the position is there to scroll to - and rAF
   *   does not run at all in a background tab, which would have made this
   *   silently do nothing for anybody whose phone had backgrounded the page.
   * - **Instant, not `behavior: 'smooth'`.** An error should be on screen when
   *   somebody looks, not a third of a second later, and a smooth scroll is
   *   one more thing a browser is allowed to throttle or skip.
   */
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [error]);

  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      role="presentation"
    >
      {/* Clicking inside the white box must NOT close it. */}
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          <h3>{title}</h3>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('common.close')}>
            <X size={20} />
          </button>
        </div>

        {/* WHY THIS LIVES HERE RATHER THAN ON THE PAGE.
            A modal covers the page and freezes it - the effect above sets
            `overflow: hidden` on the body. So a form in here that hands its
            failure to the page's own error banner has put the message behind
            a dark overlay somebody cannot even scroll to. What they see is a
            button that does nothing, which reads as the site being broken
            rather than as something they typed being too short.

            That is exactly what happened on the "create an offer" form: the
            message explaining the description was too short was drawn on the
            page underneath, invisible on a phone. Every other dialog on this
            site - Report, Suspend, Takedown, the donation box - already drew
            its own errors inside itself. This makes it the default instead of
            something each one has to remember. */}
        {error && (
          <div
            ref={errorRef}
            className="alert alert-error"
            role="alert"
            style={{ marginBottom: 'var(--space-sm)' }}
          >
            {error}
          </div>
        )}

        {children}

        {actions && <div className="modal-actions">{actions}</div>}
      </div>
    </div>
  );
}
